import axios, { type AxiosError } from 'axios';

import {
  DIAGNOSTIC_LIMITS,
  REDACTED_PATH_SEGMENT,
  REDACTED_VALUE,
  SENSITIVE_DATA_KEY_PATTERN,
  SENSITIVE_ROUTE_TEMPLATES,
} from './redaction-config';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'fatal';

export interface ApiRequestDiagnostics {
  method?: string;
  url?: string;
  body?: unknown;
}

export interface ApiResponseDiagnostics {
  status?: number;
  data?: unknown;
}

export interface PermissionRequestContext {
  projectId: number;
  projectName: string;
  permissionCodename: string;
  permissionLabel: string;
  canRequest: boolean;
  requestStatus?: 'pending';
  permissionRequestId?: number;
  requestUrl: string;
  message: string;
}

export interface AppErrorOptions {
  code?: string;
  userMessage?: string;
  severity?: ErrorSeverity;
  route?: string;
  reportable?: boolean;
  cause?: unknown;
}

const truncate = (value: string): string =>
  value.length > DIAGNOSTIC_LIMITS.maxStringLength
    ? `${value.slice(0, DIAGNOSTIC_LIMITS.maxStringLength)}… [truncated]`
    : value;

const summarizeFile = (file: File | Blob): Record<string, unknown> => ({
  type: file.type || 'unknown',
  size: file.size,
  ...('name' in file && typeof file.name === 'string' ? { name: file.name } : {}),
});

/**
 * Removes credentials and bounds payload size before diagnostics leave the browser.
 * This deliberately keeps field names and non-sensitive values because they are often
 * the difference between an actionable API report and a generic 400/500 event.
 */
export const sanitizeDiagnosticValue = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>()
): unknown => {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (typeof value === 'string') return truncate(value);
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'symbol' || typeof value === 'function') return `[${typeof value}]`;
  if (depth >= DIAGNOSTIC_LIMITS.maxDepth) return '[Max depth reached]';

  if (value instanceof Date) return value.toISOString();
  if (
    (typeof File !== 'undefined' && value instanceof File) ||
    (typeof Blob !== 'undefined' && value instanceof Blob)
  ) {
    return summarizeFile(value);
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const formData: Record<string, unknown> = {};
    for (const [key, entry] of value.entries()) {
      formData[key] = SENSITIVE_DATA_KEY_PATTERN.test(key)
        ? REDACTED_VALUE
        : sanitizeDiagnosticValue(entry, depth + 1, seen);
    }
    return formData;
  }

  if (typeof value === 'object') {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);

    if (Array.isArray(value)) {
      const sanitized = value
        .slice(0, DIAGNOSTIC_LIMITS.maxArrayItems)
        .map((item) => sanitizeDiagnosticValue(item, depth + 1, seen));
      if (value.length > DIAGNOSTIC_LIMITS.maxArrayItems) {
        sanitized.push('[Additional items truncated]');
      }
      return sanitized;
    }

    const result: Record<string, unknown> = {};
    const entries = Object.entries(value).slice(0, DIAGNOSTIC_LIMITS.maxObjectKeys);
    for (const [key, entry] of entries) {
      result[key] = SENSITIVE_DATA_KEY_PATTERN.test(key)
        ? REDACTED_VALUE
        : sanitizeDiagnosticValue(entry, depth + 1, seen);
    }
    if (Object.keys(value).length > DIAGNOSTIC_LIMITS.maxObjectKeys) {
      result.__truncated__ = 'Additional keys omitted';
    }
    return result;
  }

  return String(value);
};

const redactSensitivePath = (pathname: string): string => {
  const hasTrailingSlash = pathname.length > 1 && pathname.endsWith('/');
  const pathSegments = pathname.split('/').filter(Boolean);

  for (const template of SENSITIVE_ROUTE_TEMPLATES) {
    const templateSegments = template.split('/').filter(Boolean);
    const matches =
      templateSegments.length === pathSegments.length &&
      templateSegments.every(
        (segment, index) =>
          segment.startsWith(':') || segment.toLowerCase() === pathSegments[index]?.toLowerCase()
      );

    if (!matches) continue;

    const redactedSegments = pathSegments.map((segment, index) =>
      templateSegments[index]?.startsWith(':') ? REDACTED_PATH_SEGMENT : segment
    );
    return `/${redactedSegments.join('/')}${hasTrailingSlash ? '/' : ''}`;
  }

  return pathname;
};

/** Keeps a useful route/endpoint while removing credentials from query strings and known token URLs. */
export const sanitizeUrl = (value: string): string => {
  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(value);
    const url = new URL(value, 'https://ironview.invalid');
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_DATA_KEY_PATTERN.test(key)) url.searchParams.set(key, REDACTED_VALUE);
    }
    url.pathname = redactSensitivePath(url.pathname);
    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return redactSensitivePath(value).replace(
      /([?&][^=]*(?:password|token|secret|key)[^=]*=)[^&#]*/gi,
      '$1[Redacted]'
    );
  }
};

export const getCurrentRoute = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return sanitizeUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`);
};

export class AppError extends Error {
  readonly code: string;
  readonly userMessage: string;
  readonly severity: ErrorSeverity;
  readonly route?: string;
  readonly occurredAt: string;
  readonly reportable: boolean;
  reportId?: string;

  constructor(message: string, options: AppErrorOptions = {}) {
    super(message);
    this.name = 'AppError';
    this.code = options.code ?? 'UNEXPECTED_ERROR';
    this.userMessage = options.userMessage ?? 'Something went wrong. Please try again.';
    this.severity = options.severity ?? 'error';
    this.route = options.route ?? getCurrentRoute();
    this.occurredAt = new Date().toISOString();
    this.reportable = options.reportable ?? true;

    const captureStackTrace = (
      Error as ErrorConstructor & {
        captureStackTrace?: (target: object, constructor?: Function) => void;
      }
    ).captureStackTrace;
    captureStackTrace?.(this, AppError);

    // Copy only the stack. Holding the original Axios error as `cause` would also
    // retain headers and an unsanitized request body that an SDK could serialize.
    if (options.cause instanceof Error && options.cause.stack) {
      const originalFrames = options.cause.stack.split('\n').slice(1).join('\n');
      this.stack = `${this.name}: ${this.message}${originalFrames ? `\n${originalFrames}` : ''}`;
    }
  }
}

export interface ApiErrorOptions extends AppErrorOptions {
  status?: number;
  requestId?: string;
  backendReportId?: string;
  request?: ApiRequestDiagnostics;
  response?: ApiResponseDiagnostics;
}

export class ApiError extends AppError {
  readonly status?: number;
  readonly requestId?: string;
  readonly backendReportId?: string;
  readonly request?: ApiRequestDiagnostics;
  readonly response?: ApiResponseDiagnostics;
  /** Compatibility alias for existing call sites that inspect error.data. */
  readonly data?: unknown;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message, {
      ...options,
      code: options.code ?? (options.status ? `API_${options.status}` : 'API_NETWORK_ERROR'),
      reportable: options.reportable ?? (options.status === undefined || options.status >= 500),
    });
    this.name = 'ApiError';
    this.status = options.status;
    this.requestId = options.requestId;
    this.backendReportId = options.backendReportId;
    this.request = options.request;
    this.response = options.response;
    this.data = options.response?.data;

    if (options.cause instanceof Error && options.cause.stack) {
      const originalFrames = options.cause.stack.split('\n').slice(1).join('\n');
      this.stack = `${this.name}: ${this.message}${originalFrames ? `\n${originalFrames}` : ''}`;
    }
  }
}

const firstMessage = (value: unknown, field?: string): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return field ? `${field}: ${value.trim()}` : value.trim();
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstMessage(item, field);
      if (message) return message;
    }
  }
  if (value && typeof value === 'object') {
    for (const [nestedField, nestedValue] of Object.entries(value)) {
      const message = firstMessage(nestedValue, humanizeField(nestedField));
      if (message) return message;
    }
  }
  return undefined;
};

const humanizeField = (field: string): string =>
  field.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase());

export const getApiUserMessage = (data: unknown, status?: number): string => {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    for (const key of ['detail', 'error', 'message', 'non_field_errors']) {
      const message = firstMessage(record[key]);
      if (message) return message;
    }

    for (const [field, value] of Object.entries(record)) {
      const message = firstMessage(value, humanizeField(field));
      if (message) return message;
    }
  }

  const directMessage = firstMessage(data);
  if (directMessage && !/^request failed with status code/i.test(directMessage))
    return directMessage;

  switch (status) {
    case 400:
    case 422:
      return 'The request could not be completed. Check the submitted information and try again.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested item could not be found.';
    case 409:
      return 'This change conflicts with a newer update. Refresh and try again.';
    case 429:
      return 'Too many requests were made. Wait a moment and try again.';
    default:
      return status && status >= 500
        ? 'The server could not complete the request. The issue has been reported.'
        : 'Could not reach the server. Check your connection and try again.';
  }
};

const parseRequestBody = (data: unknown): unknown => {
  if (typeof data !== 'string') return sanitizeDiagnosticValue(data);
  try {
    return sanitizeDiagnosticValue(JSON.parse(data));
  } catch {
    return sanitizeDiagnosticValue(data);
  }
};

export const fromAxiosError = (error: AxiosError): ApiError => {
  const status = error.response?.status;
  const method = error.config?.method?.toUpperCase();
  const url = sanitizeUrl(`${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`);
  const responseData = error.response?.data;
  const responseCode =
    responseData &&
    typeof responseData === 'object' &&
    'code' in responseData &&
    typeof responseData.code === 'string'
      ? responseData.code
      : undefined;
  const responseRequestId =
    responseData &&
    typeof responseData === 'object' &&
    'request_id' in responseData &&
    typeof responseData.request_id === 'string'
      ? responseData.request_id
      : undefined;
  const responseReportId =
    responseData &&
    typeof responseData === 'object' &&
    'report_id' in responseData &&
    typeof responseData.report_id === 'string'
      ? responseData.report_id
      : undefined;
  const userMessage = getApiUserMessage(error.response?.data, status);
  const summary = `${method ?? 'API'} ${url || 'request'} failed${status ? ` with ${status}` : ''}: ${error.message}`;

  return new ApiError(summary, {
    status,
    code: responseCode,
    requestId: responseRequestId,
    backendReportId: responseReportId,
    userMessage,
    severity: status === undefined || status >= 500 ? 'error' : 'warning',
    request: {
      method,
      url: url || undefined,
      body: parseRequestBody(error.config?.data),
    },
    response: {
      status,
      data: responseData,
    },
    cause: error,
  });
};

export const normalizeError = (error: unknown): AppError => {
  if (error instanceof AppError) return error;
  if (axios.isAxiosError(error)) return fromAxiosError(error);

  if (error instanceof Error) {
    return new AppError(error.message || 'An unknown application error occurred.', {
      // Generic exception messages are developer diagnostics and may contain
      // implementation details or secrets. Deliberately authored user-facing
      // failures must use AppError/ApiError and supply `userMessage`.
      userMessage: 'Something went wrong. Please try again.',
      cause: error,
    });
  }

  if (error && typeof error === 'object') {
    const legacy = error as { message?: unknown; status?: unknown; data?: unknown };
    const status = typeof legacy.status === 'number' ? legacy.status : undefined;
    if (status !== undefined || legacy.data !== undefined) {
      const message = typeof legacy.message === 'string' ? legacy.message : 'API request failed';
      return new ApiError(message, {
        status,
        userMessage: getApiUserMessage(legacy.data, status),
        response: { status, data: legacy.data },
        cause: error,
      });
    }
  }

  return new AppError(typeof error === 'string' ? error : 'A non-Error value was thrown.', {
    cause: error,
  });
};

export const getPermissionRequestContext = (
  error: unknown,
): PermissionRequestContext | undefined => {
  const normalized = normalizeError(error);
  if (!(normalized instanceof ApiError) || normalized.code !== 'project_permission_required') {
    return undefined;
  }

  const payload = normalized.response?.data;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
  const details = (payload as Record<string, unknown>).details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return undefined;
  const record = details as Record<string, unknown>;

  if (
    typeof record.project_id !== 'number' ||
    typeof record.project_name !== 'string' ||
    typeof record.permission_codename !== 'string' ||
    typeof record.permission_label !== 'string' ||
    typeof record.can_request !== 'boolean' ||
    typeof record.request_url !== 'string'
  ) {
    return undefined;
  }

  return {
    projectId: record.project_id,
    projectName: record.project_name,
    permissionCodename: record.permission_codename,
    permissionLabel: record.permission_label,
    canRequest: record.can_request,
    requestStatus: record.request_status === 'pending' ? 'pending' : undefined,
    permissionRequestId:
      typeof record.permission_request_id === 'number'
        ? record.permission_request_id
        : undefined,
    requestUrl: record.request_url,
    message: normalized.userMessage,
  };
};

export const getErrorMessage = (error: unknown): string => normalizeError(error).userMessage;
